/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Breadcrumbs,
  Container,
  Link as BCLink,
  Box,
} from "@mui/material";
import { NavigateNext } from "@mui/icons-material";
import axios from "../../axios";
import LogsTable from "../../components/LogsTable";
import { useTranslation } from "react-i18next";
import usePermissionGuard from "../../hooks/usePermissionGuard";

// ─── Type metadata ────────────────────────────────────────────────────────────
export const TYPE_META = {
  sync_import: { label: "Sync Import", color: "#3b82f6", bg: "#eff6ff" },
  auth:        { label: "Auth",        color: "#8b5cf6", bg: "#f5f3ff" },
  store:       { label: "Store",       color: "#f59e0b", bg: "#fffbeb" },
};

export const typeMeta = (type) =>
  TYPE_META[type] ?? { label: type, color: "#6b7280", bg: "#f9fafb" };

// ─── Debounce hook ────────────────────────────────────────────────────────────
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─── Component ────────────────────────────────────────────────────────────────
const Logs = () => {
  usePermissionGuard("view_logs");
  const { t } = useTranslation();

  const [result, setResult]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [typeFilter, setType]   = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo]     = useState("");
  const [page, setPage]         = useState(1);

  const debouncedSearch = useDebounce(search, 400);
  const abortRef = useRef(null);

  const fetchLogs = useCallback((p = 1) => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    setLoading(true);
    const params = new URLSearchParams({
      page:      p,
      search:    debouncedSearch,
      type:      typeFilter,
      date_from: dateFrom,
      date_to:   dateTo,
    });

    axios
      .get(`/admin/store/logs?${params}`, { signal: abortRef.current.signal })
      .then((r) => {
        setResult(r.data);
        setLoading(false);
      })
      .catch((e) => {
        if (e?.code !== "ERR_CANCELED") setLoading(false);
      });
  }, [debouncedSearch, typeFilter, dateFrom, dateTo]);

  useEffect(() => {
    setPage(1);
    fetchLogs(1);
  }, [debouncedSearch, typeFilter, dateFrom, dateTo]);

  const handlePage = (p) => {
    setPage(p);
    fetchLogs(p);
  };

  const clearFilters = () => {
    setSearch("");
    setType("all");
    setDateFrom("");
    setDateTo("");
  };

  const hasActiveFilters = search || typeFilter !== "all" || dateFrom || dateTo;
  const types = result?.types ?? [];

  return (
    <div>
      <Container sx={{ pb: "2rem" }}>

        {/* Breadcrumb */}
        <div role="presentation">
          <Breadcrumbs
            aria-label="breadcrumb"
            separator={<NavigateNext fontSize="small" />}
            sx={{ p: "14px 0" }}
          >
            <BCLink underline="none" color="inherit" href="/" fontSize="14px">
              {t("Home")}
            </BCLink>
            <BCLink
              underline="none"
              color="text.primary"
              href="/store/logs"
              aria-current="page"
              fontSize="14px"
            >
              {t("Logs")}
            </BCLink>
          </Breadcrumbs>
        </div>

        {/* Page header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#0f172a" }}>
              {t("Logs")}
            </h1>
            {result?.data && (
              <span style={{ fontSize: 13, color: "#94a3b8", fontWeight: 500 }}>
                {result.data.total.toLocaleString()} {t("entries")}
              </span>
            )}
          </Box>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>
            {t("Activity and sync events for your store.")}
          </p>
        </Box>

        {/* Filter bar */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            mb: 2.5,
            p: "14px 16px",
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: "10px",
            alignItems: "center",
          }}
        >
          {/* Search */}
          <Box sx={{ position: "relative", flex: "1 1 220px", minWidth: 180 }}>
            <svg
              style={{
                position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
                width: 15, height: 15, color: "#94a3b8", pointerEvents: "none",
              }}
              fill="none" stroke="currentColor" strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("Search messages…")}
              style={{
                width: "100%", boxSizing: "border-box",
                paddingLeft: 32, paddingRight: 12, paddingTop: 8, paddingBottom: 8,
                border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13,
                outline: "none", color: "#1e293b", background: "#f8fafc",
                transition: "border-color .15s",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#6366f1")}
              onBlur={(e)  => (e.target.style.borderColor = "#e2e8f0")}
            />
          </Box>

          {/* Type filter */}
          <select
            value={typeFilter}
            onChange={(e) => setType(e.target.value)}
            style={{
              flex: "0 0 auto", padding: "8px 12px",
              border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13,
              color: "#1e293b", background: "#f8fafc", cursor: "pointer", outline: "none",
            }}
          >
            <option value="all">{t("All types")}</option>
            {types.map((tp) => (
              <option key={tp} value={tp}>{typeMeta(tp).label}</option>
            ))}
          </select>

          {/* Date from */}
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            title={t("From date")}
            style={{
              flex: "0 0 auto", padding: "8px 10px",
              border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13,
              color: dateFrom ? "#1e293b" : "#94a3b8", background: "#f8fafc", outline: "none",
            }}
          />
          <span style={{ color: "#cbd5e1", fontSize: 13, userSelect: "none" }}>→</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            title={t("To date")}
            style={{
              flex: "0 0 auto", padding: "8px 10px",
              border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13,
              color: dateTo ? "#1e293b" : "#94a3b8", background: "#f8fafc", outline: "none",
            }}
          />

          {/* Clear */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              style={{
                flex: "0 0 auto", padding: "8px 14px",
                border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13,
                color: "#64748b", background: "#fff", cursor: "pointer",
                fontWeight: 500, transition: "background .15s",
              }}
              onMouseEnter={(e) => (e.target.style.background = "#f8fafc")}
              onMouseLeave={(e) => (e.target.style.background = "#fff")}
            >
              {t("Clear filters")}
            </button>
          )}
        </Box>

        {/* Table */}
        <LogsTable
          data={result?.data ?? null}
          loading={loading}
          typeMeta={typeMeta}
          page={page}
          onPageChange={handlePage}
        />

      </Container>
    </div>
  );
};

export default React.memo(Logs);
